/**
 * Lead Sources — the owner's window into Kate's lead research.
 *
 * Two jobs: show what is being worked and how regularly (Sources tab, tile
 * row), and put Kate's "is this interesting?" questions in front of the
 * owner (Leads tab, default). "Interesting" hands the lead to Seth (growth)
 * as a delegated task with a short not-before window, so Undo works until he
 * picks it up. Kate organises her own work in her seat repo; nothing here
 * edits sources — she reports them through the `leadsource_*` tools.
 *
 * A third view, Register watch, shows the server's watch over new HRB entries
 * (`RegisterWatchPanel`) — the evidence link of a register-sourced lead opens
 * its entry there.
 *
 * URL state: `?view=leads|sources|register`, `?status=` (leads filter,
 * default awaiting), `?source=<slug>` (drawer), `?entry=<id>` (register entry).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { clsx } from "clsx";

import { leadSourcesApi, type LeadCandidate, type Verdict } from "../api/leadSources";
import { useToast } from "../components/toast/context";
import { LeadsTable } from "../components/leads/LeadsTable";
import { RegisterWatchPanel } from "../components/leads/RegisterWatchPanel";
import { SourceDrawer } from "../components/leads/SourceDrawer";
import { SourcesTable } from "../components/leads/SourcesTable";
import { PageHeader } from "../components/PageHeader";
import { QueryBoundary } from "../components/QueryBoundary";
import { StatTile } from "../components/StatTile";
import { FilterChips } from "../components/ui/FilterChips";
import { LEAD_FILTERS, type LeadFilter, type LeadView } from "../lib/leadSources";

const POLL_MS = 30_000;
const KEY = ["lead-sources"] as const;

const VIEWS: { value: LeadView; label: string }[] = [
  { value: "leads", label: "Leads" },
  { value: "sources", label: "Sources" },
  { value: "register", label: "Register watch" },
];

const VERDICT_TOAST: Record<Verdict, string> = {
  interesting: "Handed to Seth – Undo works until he picks it up.",
  not_interesting: "Marked not interesting – Kate reads your note.",
  proposed: "Back to undecided.",
};

export function LeadSourcesPage() {
  const [sp, setSp] = useSearchParams();
  const viewParam = sp.get("view");
  const view: LeadView = viewParam === "sources" || viewParam === "register" ? viewParam : "leads";
  const statusParam = sp.get("status");
  const status: LeadFilter = LEAD_FILTERS.some((f) => f.value === statusParam)
    ? (statusParam as LeadFilter)
    : "proposed";
  const sourceSlug = sp.get("source");

  const qc = useQueryClient();
  const toast = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: [...KEY, "overview"],
    queryFn: leadSourcesApi.overview,
    refetchInterval: POLL_MS,
  });
  const leads = useQuery({
    queryKey: [...KEY, "leads", status],
    queryFn: () => leadSourcesApi.leads({ status }),
    refetchInterval: POLL_MS,
    enabled: view === "leads",
  });
  const sources = useQuery({
    queryKey: [...KEY, "sources"],
    queryFn: leadSourcesApi.sources,
    refetchInterval: POLL_MS,
    enabled: view === "sources",
  });
  const sourceDetail = useQuery({
    queryKey: [...KEY, "source", sourceSlug],
    queryFn: () => leadSourcesApi.source(sourceSlug as string),
    enabled: !!sourceSlug,
  });

  const verdict = useMutation({
    mutationFn: ({ lead, value, note }: { lead: LeadCandidate; value: Verdict; note?: string }) =>
      leadSourcesApi.verdict(lead.id, value, note),
    onMutate: ({ lead }) => setBusyId(lead.id),
    onSettled: () => setBusyId(null),
    onSuccess: (_data, { value }) => {
      toast.ok(VERDICT_TOAST[value]);
      return qc.invalidateQueries({ queryKey: KEY });
    },
    onError: (err) => toast.error(err),
  });

  async function onVerdict(lead: LeadCandidate, value: Verdict, note?: string) {
    await verdict.mutateAsync({ lead, value, note }).catch(() => undefined);
  }

  function setParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setSp(next);
  }

  function viewHref(value: LeadView): string {
    const next = new URLSearchParams(sp);
    next.set("view", value);
    return `?${next.toString()}`;
  }

  const o = overview.data;
  return (
    <div className="page">
      <PageHeader
        title="Lead Sources"
        description="Lists Kate works on a cadence – association members, accelerator and VC portfolios, event exhibitors – and the organisations she asks you about. Interesting ones go to Seth, who finds contacts and enrolls them."
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatTile
          label="Awaiting you"
          figure={o ? String(o.leads_awaiting) : "…"}
          deltaTone={o && o.leads_awaiting > 0 ? "warn" : null}
          delta={o && o.leads_awaiting > 0 ? "decide in Leads" : undefined}
        />
        <StatTile label="Interesting · 30 d" figure={o ? String(o.interesting_30d) : "…"} />
        <StatTile label="With Seth" figure={o ? String(o.with_seth) : "…"} />
        <StatTile
          label="Sources active"
          figure={o ? String(o.sources_active) : "…"}
          deltaTone={o && o.sources_overdue > 0 ? "warn" : null}
          delta={o ? (o.sources_overdue > 0 ? `${o.sources_overdue} overdue` : "all on cadence") : undefined}
        />
        <StatTile
          label="Blocked"
          figure={o ? String(o.sources_blocked) : "…"}
          deltaTone={o && o.blocked_awaiting_review > 0 ? "warn" : null}
          delta={
            o && o.blocked_awaiting_review > 0 ? (
              <Link to="/tasks" className="link">
                {o.blocked_awaiting_review} to review by hand
              </Link>
            ) : undefined
          }
        />
      </div>

      <nav className="subnav" aria-label="Lead Sources views">
        {VIEWS.map((v) => (
          <Link key={v.value} to={viewHref(v.value)} className={clsx(view === v.value && "is-active")}>
            {v.label}
          </Link>
        ))}
      </nav>

      {view === "register" ? (
        <RegisterWatchPanel entryId={sp.get("entry")} onOpenEntry={(id) => setParams({ entry: id })} />
      ) : view === "leads" ? (
        <>
          <div className="flex items-center gap-1.5 mb-4 flex-wrap">
            <FilterChips
              aria-label="Lead status"
              options={LEAD_FILTERS.map((f) => ({
                value: f.value,
                label: f.label,
                count: leads.data && f.value !== "all" ? leads.data.counts[f.value] : null,
              }))}
              value={status}
              onChange={(value) => setParams({ status: value })}
            />
          </div>
          <QueryBoundary
            query={leads}
            isEmpty={(d) => d.items.length === 0}
            empty={
              <div className="empty-state">
                <div className="title">
                  {status === "proposed" ? "Nothing waiting for you" : "No leads here"}
                </div>
                <p className="hint">
                  Kate proposes organisations from her lead sources as she works them.
                </p>
              </div>
            }
          >
            {(data) => (
              <LeadsTable
                leads={data.items}
                busyId={busyId}
                onVerdict={onVerdict}
                onOpenSource={(slug) => setParams({ source: slug })}
              />
            )}
          </QueryBoundary>
        </>
      ) : (
        <QueryBoundary
          query={sources}
          isEmpty={(d) => d.length === 0}
          empty={
            <div className="empty-state">
              <div className="title">No sources registered yet</div>
              <p className="hint">Kate registers each list she works with leadsource_upsert.</p>
            </div>
          }
        >
          {(data) => <SourcesTable sources={data} onOpen={(slug) => setParams({ source: slug })} />}
        </QueryBoundary>
      )}

      <SourceDrawer
        open={!!sourceSlug}
        detail={sourceDetail.data}
        loading={sourceDetail.isLoading}
        onClose={() => setParams({ source: null })}
      />
    </div>
  );
}
